import { useEffect, useState } from 'react';
import {
  FilterInput,
  FieldOption,
  LogicalOperator,
  OperatorOption,
} from '../../constants';
import {
  parseRangeCompositeValue,
  RangeCompositeValue,
  stringifyRangeCompositeValue,
} from '../rangeValue';
import { getNonEmptyFilters } from './basicSearchConditionUtils';

interface UseBasicSearchConditionHandlersParams {
  field: (typeof FieldOption)[keyof typeof FieldOption];
  getUniqueFilters: () => FilterInput[];
  setFiltersToUse: (filters: FilterInput[]) => void;
  updateCommonFilterOperatorToUse: (operator: LogicalOperator) => void;
  operator: string;
  value: string;
  isBetweenOperator: boolean;
  isDateField: boolean;
}

export function useBasicSearchConditionHandlers({
  field,
  getUniqueFilters,
  setFiltersToUse,
  updateCommonFilterOperatorToUse,
  operator,
  value,
  isBetweenOperator,
  isDateField,
}: UseBasicSearchConditionHandlersParams) {
  const [rangeInputs, setRangeInputs] = useState<RangeCompositeValue>(() => {
    if (!isBetweenOperator) return {};
    const parsed = parseRangeCompositeValue(value);
    return {
      min: parsed?.min || '',
      max: parsed?.max || '',
    };
  });

  useEffect(() => {
    if (!isBetweenOperator) return;
    const parsed = parseRangeCompositeValue(value);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-syncs the between-range min/max when the value prop changes externally; rangeInputs is also written by handleRangeChange and handleChangeOperator, so it is not purely derived
    setRangeInputs({
      min: parsed?.min || '',
      max: parsed?.max || '',
    });
  }, [isBetweenOperator, value]);

  function handleChangeOperator(nextOperator: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!match) {
      uniqueFilters.push({
        field: field.value,
        operator: nextOperator,
        value: '',
      });
    } else {
      const previousOperator = match.operator;
      match.operator = nextOperator;

      if (nextOperator === OperatorOption.isNotEmpty.value) {
        match.value = '';
      }

      if (
        nextOperator === OperatorOption.between.value ||
        previousOperator === OperatorOption.between.value
      ) {
        match.value = '';
        setRangeInputs({ min: '', max: '' });
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);

    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleChangeValue(value: string) {
    const uniqueFilters = getUniqueFilters();

    // apply value change
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!match) {
      uniqueFilters.push({
        field: field.value,
        operator,
        value,
      });
    } else {
      match.value = value;
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);

    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleRangeChange(
    inputKey: keyof RangeCompositeValue,
    inputValue: string,
  ) {
    const newInputs = { ...rangeInputs, [inputKey]: inputValue };
    const hasValue = !!(newInputs.min?.trim() || newInputs.max?.trim());

    setRangeInputs(newInputs);
    handleChangeValue(hasValue ? stringifyRangeCompositeValue(newInputs) : '');
  }

  // Animal / dam / sire fields use the chip-style multi-value picker. Each
  // chip encodes its own comparison operator via wildcard pattern, so the
  // stored row has `operator: ''` and the serialised chip list as value.
  function handleMemberIdentChipChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else if (!match) {
      uniqueFilters.push({
        field: field.value,
        operator: '',
        value: nextValue,
      });
    } else {
      match.operator = '';
      match.value = nextValue;
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleAnimalIdentChipChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else if (!match) {
      uniqueFilters.push({
        field: field.value,
        operator: '',
        value: nextValue,
      });
    } else {
      match.operator = '';
      match.value = nextValue;
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleSexValueChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else {
      if (!match) {
        uniqueFilters.push({
          field: field.value,
          operator: OperatorOption.equals.value,
          value: nextValue,
        });
      } else {
        match.operator = OperatorOption.equals.value;
        match.value = nextValue;
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleTattooValueChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else {
      if (!match) {
        uniqueFilters.push({
          field: field.value,
          operator: OperatorOption.startsWith.value,
          value: nextValue,
        });
      } else {
        match.operator = OperatorOption.startsWith.value;
        match.value = nextValue;
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleCalvingYearValueChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else {
      if (!match) {
        uniqueFilters.push({
          field: field.value,
          operator: OperatorOption.equals.value,
          value: nextValue,
        });
      } else {
        match.operator = OperatorOption.equals.value;
        match.value = nextValue;
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  function handleRegistrationStatusValueChange(nextValue: string) {
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!nextValue) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else {
      if (!match) {
        uniqueFilters.push({
          field: field.value,
          operator: OperatorOption.equals.value,
          value: nextValue,
        });
      } else {
        match.operator = OperatorOption.equals.value;
        match.value = nextValue;
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  const [dateRangeInputs, setDateRangeInputs] = useState<RangeCompositeValue>(
    () => {
      if (!isDateField) return {};
      if (operator === OperatorOption.between.value) {
        const parsed = parseRangeCompositeValue(value);
        return { min: parsed?.min || '', max: parsed?.max || '' };
      }
      if (
        operator === OperatorOption.greaterThan.value ||
        operator === OperatorOption.greaterThanOrEqual.value
      ) {
        return { min: value, max: '' };
      }
      if (
        operator === OperatorOption.lessThan.value ||
        operator === OperatorOption.lessThanOrEqual.value
      ) {
        return { min: '', max: value };
      }
      return {};
    },
  );

  useEffect(() => {
    if (!isDateField) return;
    if (operator === OperatorOption.between.value) {
      const parsed = parseRangeCompositeValue(value);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- re-syncs the date-range inputs from operator/value props when they change externally; dateRangeInputs is also written by handleDateRangeChange, so it is not purely derived
      setDateRangeInputs({ min: parsed?.min || '', max: parsed?.max || '' });
    } else if (
      operator === OperatorOption.greaterThan.value ||
      operator === OperatorOption.greaterThanOrEqual.value
    ) {
      setDateRangeInputs({ min: value, max: '' });
    } else if (
      operator === OperatorOption.lessThan.value ||
      operator === OperatorOption.lessThanOrEqual.value
    ) {
      setDateRangeInputs({ min: '', max: value });
    } else if (!operator && !value) {
      setDateRangeInputs({});
    }
  }, [isDateField, operator, value]);

  function handleDateRangeChange(
    inputKey: keyof RangeCompositeValue,
    inputValue: string,
  ) {
    const newInputs = { ...dateRangeInputs, [inputKey]: inputValue };
    const hasMin = !!newInputs.min?.trim();
    const hasMax = !!newInputs.max?.trim();

    setDateRangeInputs(newInputs);

    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);

    if (!hasMin && !hasMax) {
      if (match) {
        match.operator = '';
        match.value = '';
      }
    } else {
      const filterUpdate = {
        field: field.value,
        operator: OperatorOption.between.value,
        value: stringifyRangeCompositeValue(newInputs),
      };
      if (!match) {
        uniqueFilters.push(filterUpdate);
      } else {
        match.operator = filterUpdate.operator;
        match.value = filterUpdate.value;
      }
    }

    const nonEmptyFilters = getNonEmptyFilters(uniqueFilters);
    setFiltersToUse(nonEmptyFilters);
    updateCommonFilterOperatorToUse(LogicalOperator.and);
  }

  return {
    rangeInputs,
    dateRangeInputs,
    handleChangeOperator,
    handleChangeValue,
    handleRangeChange,
    handleMemberIdentChipChange,
    handleAnimalIdentChipChange,
    handleSexValueChange,
    handleTattooValueChange,
    handleCalvingYearValueChange,
    handleRegistrationStatusValueChange,
    handleDateRangeChange,
  };
}
